import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Save, CalendarDays, BookUp, Archive } from 'lucide-react';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import { toast } from 'sonner';

const KEYS = [
  'semesterar_start_month',
  'semesterskuld_booking_cadence',
  'time_entry_history_retention_years',
];

const DEFAULTS = {
  semesterar_start_month: 4,
  semesterskuld_booking_cadence: 'year_end',
  time_entry_history_retention_years: 7,
};

const MONTH_OPTIONS = [
  { value: 1, label: 'Januari — kalenderår' },
  { value: 2, label: 'Februari' },
  { value: 3, label: 'Mars' },
  { value: 4, label: 'April — Semesterlagens standard' },
  { value: 5, label: 'Maj' },
  { value: 6, label: 'Juni' },
  { value: 7, label: 'Juli — Juli-baserat semesterår' },
  { value: 8, label: 'Augusti' },
  { value: 9, label: 'September' },
  { value: 10, label: 'Oktober' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const CADENCE_OPTIONS = [
  { value: 'year_end', label: 'Bokslut (rekommenderat för K2)' },
  { value: 'monthly', label: 'Månatligen' },
  { value: 'per_payroll', label: 'Per lönekörning' },
  { value: 'manual', label: 'Manuellt' },
];

const PayrollCompanySettings = () => {
  const { companyId } = useParams();
  const [values, setValues] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.post(
        getApiUrl(`${companyId}/settings/bulk`),
        { keys: KEYS },
        { withCredentials: true },
      );
      const remote = res.data?.settings || {};
      setValues({
        semesterar_start_month: parseInt(remote.semesterar_start_month, 10)
          || DEFAULTS.semesterar_start_month,
        semesterskuld_booking_cadence: remote.semesterskuld_booking_cadence
          || DEFAULTS.semesterskuld_booking_cadence,
        time_entry_history_retention_years: parseInt(
          remote.time_entry_history_retention_years, 10,
        ) || DEFAULTS.time_entry_history_retention_years,
      });
      setDirty(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte ladda inställningar.');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const updateField = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    const years = parseInt(values.time_entry_history_retention_years, 10);
    if (Number.isNaN(years) || years < 1 || years > 20) {
      toast.error('Bevarande-år måste vara 1–20. Bokföringslagen kräver minst 7.');
      return;
    }
    if (years < 7) {
      const ok = window.confirm(
        `Bokföringslagen (7 kap 2§) kräver att bokföringsmaterial bevaras i minst 7 år. `
        + `Du har valt ${years} år. Fortsätt ändå?`,
      );
      if (!ok) return;
    }
    try {
      setSaving(true);
      await axios.post(
        getApiUrl(`${companyId}/settings/bulk/update`),
        {
          updates: [
            { key: 'semesterar_start_month', value: parseInt(values.semesterar_start_month, 10) },
            { key: 'semesterskuld_booking_cadence', value: values.semesterskuld_booking_cadence },
            { key: 'time_entry_history_retention_years', value: years },
          ],
        },
        { withCredentials: true },
      );
      toast.success('Löneinställningar sparade.');
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Kunde inte spara.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-4">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="modern-card mt-4">
      <div className="modern-card-header">
        <div>
          <h2 className="section-title">
            <BookUp size={24} />
            Löner &amp; semester
          </h2>
          <p className="section-subtitle">
            Konfiguration för semesterår, semesterskuld och bevarandetid för tidrapporter.
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="m-3">{error}</Alert>
      )}

      <div className="modern-card-body">
        <Form>
          <Form.Group className="mb-4">
            <Form.Label className="d-flex align-items-center gap-2">
              <CalendarDays size={16} />
              <strong>Semesterårets startmånad</strong>
            </Form.Label>
            <Form.Text className="text-muted d-block mb-2">
              Styr vilken månad intjänandeåret börjar. Semesterlagen (1977:480) §3
              använder 1 april som standard; många företag följer kalenderår (1 januari)
              eller juli-baserat år.
            </Form.Text>
            <Form.Select
              value={values.semesterar_start_month}
              onChange={(e) => updateField('semesterar_start_month', parseInt(e.target.value, 10))}
            >
              {MONTH_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label className="d-flex align-items-center gap-2">
              <BookUp size={16} />
              <strong>Bokföringskadens för semesterskuld (2940)</strong>
            </Form.Label>
            <Form.Text className="text-muted d-block mb-2">
              Vägledning för när justering av semesterlöneskuld ska bokföras.
              K2-företag bokför typiskt endast vid bokslut; K3 kan kräva månadsvis
              periodisering.
            </Form.Text>
            <Form.Select
              value={values.semesterskuld_booking_cadence}
              onChange={(e) => updateField('semesterskuld_booking_cadence', e.target.value)}
            >
              {CADENCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label className="d-flex align-items-center gap-2">
              <Archive size={16} />
              <strong>Bevarandetid för tidrapport-historik (år)</strong>
            </Form.Label>
            <Form.Text className="text-muted d-block mb-2">
              Hur länge audit-rader i <code>time_entry_history</code> bevaras.
              Bokföringslagen (7 kap 2§) kräver <strong>minst 7 år</strong>.
              Ett lägre värde kan tillåtas för ej bokföringskopplade rader under
              GDPR-dataminimering (art. 5.1e).
            </Form.Text>
            <Form.Control
              type="number"
              min={1}
              max={20}
              value={values.time_entry_history_retention_years}
              onChange={(e) => updateField(
                'time_entry_history_retention_years',
                e.target.value === '' ? '' : parseInt(e.target.value, 10),
              )}
            />
          </Form.Group>

          <div className="d-flex justify-content-end">
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!dirty || saving}
              className="d-flex align-items-center gap-2"
            >
              {saving ? <Spinner size="sm" animation="border" /> : <Save size={16} />}
              Spara
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default PayrollCompanySettings;
